// NEW FORM BUILDER COMPONENT

import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { DropResult } from '@hello-pangea/dnd';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Form, useFormik, FormikProvider } from 'formik';
import { Position, DraggableData, ResizableDelta } from 'react-rnd';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import RestoreIcon from '@mui/icons-material/Restore';
import {
  Box,
  Card,
  Grid,
  Button,
  Switch,
  Tooltip,
  Checkbox,
  FormGroup,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
  FormControlLabel,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';

import { Tag } from 'src/@types/tags/tag';

import FormSettings from './FormSettings';
import FormPdfViewer from './FormPdfViewer';
import reorder from '../approvers/utils/reorder';
import FormRelatedForms from './FormRelatedForms';
import { useSelector } from '../../../redux/store';
import { PATH_ADMIN } from '../../../routes/paths';
import FormsElementButton from './FormsElementButton';
import FormEmailReceivers from './FormEmailReceivers';
import FormApprovers from '../approvers/FormApprovers';
import FormWorkflowBinding from './FormWorkflowBinding';
import useAppSettings from '../../../hooks/useAppSettings';
import TypeAddEditDialog from '../shared/TypeAddEditDialog';
import useNotifications from '../../../hooks/useNotifications';
import { getDepartments } from '../../../redux/slices/departments';
import { Department } from '../../../@types/departments/departments';
import { ModuleTypes } from '../../../@types/module/enums/moduleTypes';
import ModuleCheck from '../../../components/access-checks/ModuleCheck';
import CollaboratorsEditForm from '../collaborators/CollaboratorsEditForm';
import { FormElementType } from '../../../@types/forms/enums/FormElementType';
import { FormApprovalType } from '../../../@types/forms/enums/FormApprovalType';
import { ValidationPatterns } from '../../../utils/constants/validationPatterns';
import { FormProps, TypeProps, ModuleProps } from '../create-form/FormsCreateNewForm';
import { getFile, getForm, clearFormsState } from '../../../redux/slices/formBuilder';
import { DocumentModuleTypes } from '../../../@types/shared/enums/documentModuleTypes';
import { ELEMENT_NO_VALIDATION } from '../../../@types/forms/constants/ElementValidations';
import { bindWorkflow, unbindWorkflow } from '../../../services/workflow/workflowBindingService';
import { editFormById, getFormTypes, getFormTemplatesList } from '../../../services/formService';
import {
  TextStyle,
  FontFamily,
  TextAlignment,
  TextDecoration,
} from '../../../@types/forms/enums/ElementProperties';
import {
  Collaborator,
  ElementProps,
  DocumentPageSize,
  FormApprovalStepProps,
  FormTypeTemplateProps,
  DraggableApproverProps,
} from '../../../@types/forms/types';

// ----------------------------------------------------------------------

type FormsEditFormProps = {
  titleLocale: string;
};

type RelatedFormTemplatesProps = {
  id: number;
  name: string;
};

type FormViewportProps = {
  pageNumber: number;
  width: number;
  height: number;
  scale: number;
};

const FormsEditForm: React.FC<FormsEditFormProps> = ({ titleLocale }) => {
  const { t } = useLocales();
  const modulesData = useAppSettings();
  const { form, file } = useSelector((state) => state.formBuilder);
  const { enqueueSuccess, enqueueError } = useNotifications();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [availableModules, setAvailableModules] = useState<ModuleProps[]>([
    {
      formModuleType: ModuleTypes.HR,
      moduleTitle: 'HR',
    },
  ]);
  const [availableTypes, setAvailableTypes] = useState<TypeProps[]>([]);
  const [selectedModule, setSelectedModule] = useState<ModuleProps | null>(null);
  const [selectedType, setSelectedType] = useState<TypeProps | null>(null);
  const [layerSize, setLayerSize] = useState<DocumentPageSize | null>(null);
  const [formViewport, setFormViewport] = useState<FormViewportProps[] | null>(null);
  const [elements, setElements] = useState<ElementProps[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isAddTypeDialogOpen, setIsAddTypeDialogOpen] = useState<boolean>(false);
  const [selectedElement, setSelectedElement] = useState<ElementProps | null>(null);
  const [, setDragResizeElement] = useState<string | null>(null);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isRelatedFormsExpanded, setIsRelatedFormsExpanded] = useState(false);
  const [isEmailReceiversExpanded, setIsEmailReceiversExpanded] = useState(false);
  const [pageNumberScale, setPageNumberScale] = useState(1);
  const [formTemplatesList, setFormTemplatesList] = useState<RelatedFormTemplatesProps[]>([]);
  const [selectedFormTemplate, setSelectedFormTemplate] =
    useState<RelatedFormTemplatesProps | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [relatedFormTemplates, setRelatedFormTemplates] = useState<number[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const elementLayer = useRef<HTMLElement | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [formTags, setFormTags] = useState<Tag[]>([]);
  const { departments } = useSelector((state) => state.departments);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [emailers, setEmailers] = useState<string[]>([]);
  const [customApprovers, setCustomApprovers] = useState<string[]>([]);
  const [isEditMode, setIsEditMode] = useState(true);
  const [formApprovalSteps, setFormApprovalSteps] = useState<FormApprovalStepProps[]>([]);
  const [orderOfApprovers, setOrderOfApprovers] = useState<DraggableApproverProps[]>([]);
  const [orderOfIntegrationApprovers, setOrderOfIntegrationApprovers] = useState<
    DraggableApproverProps[]
  >([]);
  const [isDocLogixIntegration, setIsDocLogixIntegration] = useState(false);
  const [workflowDefinitionNumber, setWorkflowDefinitionNumber] = useState<number | ''>('');
  const savedWorkflowDefinitionNumber = useRef<number | ''>('');

  const hasApprovers = (
    isDocLogixIntegration ? orderOfIntegrationApprovers : orderOfApprovers
  ).some((approver) => approver.checked);

  const hasWorkflow = workflowDefinitionNumber !== '';
  const [deletedElements, setDeletedElements] = useState<ElementProps[]>([]);
  const [agreeDelete, setAgreeDelete] = useState(false);

  const DLX_APPROVERS: DraggableApproverProps[] = [
    {
      id: 'doclogix-approval',
      approvalType: FormApprovalType.DocLogixApproval,
      approvalName: t('formTemplates.fields.approvalInDoclogix'),
      checked: true,
      order: 1,
      signerEmails: [],
    },
    {
      id: 'manager-approval-doclogix',
      approvalType: FormApprovalType.ManagerApproval,
      approvalName: t('formTemplates.fields.managerApproval'),
      checked: false,
      order: 2,
      signerEmails: [],
    },
  ];

  const APPROVERS: DraggableApproverProps[] = [
    {
      id: 'manager-approval',
      approvalType: FormApprovalType.ManagerApproval,
      approvalName: t('formTemplates.fields.managerApproval'),
      checked: false,
      order: 1,
      signerEmails: [],
    },
    {
      id: 'custom-approval',
      approvalType: FormApprovalType.DefaultSignersApproval,
      approvalName: t('forms.customApprovers'),
      checked: false,
      order: 2,
      signerEmails: [],
    },
    {
      id: 'ceo-approval',
      approvalType: FormApprovalType.CEOApproval,
      approvalName: t('formTemplates.fields.ceoApproval'),
      checked: false,
      order: 3,
      signerEmails: [],
    },
    {
      id: 'owner-approval',
      approvalType: FormApprovalType.OwnerApproval,
      approvalName: t('formTemplates.fields.ownerApproval'),
      checked: false,
      order: 4,
      signerEmails: [],
    },
  ];

  const baseElementInitValues = {
    doclogixCoreAttributeShortName: null,
    x: 12,
    y: 36,
    width: 164,
    height: 1,
    font: FontFamily.Arial,
    fontSize: 12,
    textAlignment: TextAlignment.Left,
    textStyle: TextStyle.Regular,
    textDecoration: TextDecoration.None,
    color: '#000',
    rotate: 0,
    required: false,
    validations: ELEMENT_NO_VALIDATION,
  };

  const staticTextInitValues = {
    label: t('forms.elements.StaticText'),
    staticText: '',
    enableResizing: { left: true, right: true },
  };

  const inputFieldInitValues = {
    label: t('forms.elements.InputField'),
    inputType: 'text',
    numberOfCharacters: [5, 15],
    enableResizing: { left: true, right: true },
  };

  const dropdownInitValues = {
    label: t('forms.elements.Dropdown'),
    enableResizing: { left: true, right: true },
    options: [],
  };

  const checkboxInitValues = {
    label: t('forms.elements.Checkbox'),
    enableResizing: { left: true, right: true },
    options: [],
  };

  const radioInitValues = {
    label: t('forms.elements.RadioButton'),
    enableResizing: { left: true, right: true },
    options: [],
  };

  const dateInitValues = {
    label: t('forms.elements.Date'),
    enableResizing: { left: true, right: true },
    disablePast: false,
  };

  const dateRangeInitValues = {
    label: t('forms.elements.DateRange'),
    enableResizing: { left: true, right: true },
    disablePast: false,
    calendarEventType: null,
  };

  const customListInitValues = {
    label: t('forms.elements.CustomList'),
    enableResizing: { left: true, right: true },
    list: null,
  };

  const addressBookInitValues = {
    label: t('forms.elements.AddressBook'),
    enableResizing: { left: true, right: true },
    isForExternal: false,
    isForInternal: false,
    isForAll: false,
  };

  const imageInitValues = {
    label: t('forms.elements.Image'),
    enableResizing: { left: true, right: true },
  };

  const hyperlinkInitValues = {
    label: t('forms.elements.Hyperlink'),
    enableResizing: { left: true, right: true },
  };

  const signatureInitValues = {
    label: t('forms.elements.Signature'),
    height: 80,
    enableResizing: {
      bottom: true,
      bottomLeft: true,
      bottomRight: true,
      left: true,
      right: true,
      top: true,
      topLeft: true,
      topRight: true,
    },
  };

  const userInitValues = {
    label: t('forms.elements.User'),
    enableResizing: { left: true, right: true },
  };

  const registrationNumberInitValues = {
    label: t('forms.elements.RegistrationNumber'),
    enableResizing: { left: true, right: true },
    prefix: '',
  };

  const validationSchema = Yup.object().shape({
    id: Yup.number(),
    name: Yup.string().required(t('shared.errors.nameRequired')),
    formModuleType: Yup.number()
      .min(0, t('shared.errors.moduleRequired'))
      .required(t('shared.errors.moduleRequired')),
    formTypeId: Yup.number()
      .min(0, t('shared.errors.typeRequired'))
      .required(t('shared.errors.typeRequired')),
    approvalInDoclogix: Yup.boolean(),
    managerApproval: Yup.boolean(),
    ceoApproval: Yup.boolean(),
    doclogixCoreUrlShortname: Yup.string(),
    doclogixCoreProfileId: Yup.number(),
    doclogixCoreFileId: Yup.number(),
    relatedFormTemplates: Yup.array().of(Yup.number()),
    departmentIds: Yup.array().of(Yup.number()),
    sortWeight: Yup.string().matches(ValidationPatterns.numbers, t('shared.errors.onlyNumbers')),
    defaultFormTemplate: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      id: -1,
      name: '',
      formModuleType: -1,
      formTypeId: -1,
      approvalInDoclogix: false,
      managerApproval: false,
      ceoApproval: false,
      sortWeight: 0,
      doclogixCoreUrlShortname: '',
      doclogixCoreProfileId: -1,
      doclogixCoreFileId: -1,
      relatedFormTemplates,
      departmentIds,
      tags: formTags.length > 0 ? formTags.map((item) => item.id) : [],
      defaultFormTemplate: false,
      approvalSteps: [],
    },

    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      const tagsForSend = formTags.length > 0 ? formTags.map((item) => item.id) : [];

      const model = {
        id: values.id,
        name: values.name,
        formModuleType: values.formModuleType,
        formTypeId: values.formTypeId,
        approvalInDoclogix: values.approvalInDoclogix,
        managerApproval: values.managerApproval,
        ceoApproval: values.ceoApproval,
        sortWeight: values.sortWeight,
        doclogixCoreUrlShortname: values.doclogixCoreUrlShortname || undefined,
        doclogixCoreProfileId: values.doclogixCoreProfileId,
        doclogixCoreFileId: values.doclogixCoreFileId,
        relatedFormTemplates,
        departmentIds,
        structure: JSON.stringify(elements),
        tags: tagsForSend,
        mailGetterEmails: emailers,
        defaultSigners: customApprovers,
        defaultFormTemplate: values.defaultFormTemplate,
        // approvalSteps: isDocLogixIntegration ? DLX_APPROVERS_STEPS : formApprovalSteps,
        approvalSteps: formApprovalSteps,
        collaborators: collaborators
          .filter((c) => c.role === 'owner' || c.fields.length > 0)
          .map((c, i) => ({
            formTemplateId: values.id,
            collaboratorId: c.collaboratorId,
            role: c.role,
            fields: c.fields,
            color: c.color,
            canFill: c.canFill,
            order: i,
          })),
      } as unknown as FormProps;

      try {
        await editFormById(model);
        await saveWorkflowBinding();
        enqueueSuccess(t('forms.snackbar.formCreated'));
        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);
        setErrors(error);
      } finally {
        clearState();
        clearFormsState();
        navigate(PATH_ADMIN.forms);
      }
    },
  });

  const handleWorkflowLoaded = (definitionNumber: number | '') => {
    savedWorkflowDefinitionNumber.current = definitionNumber;
    setWorkflowDefinitionNumber(definitionNumber);
  };

  const saveWorkflowBinding = async () => {
    if (!id || workflowDefinitionNumber === savedWorkflowDefinitionNumber.current) return;

    try {
      if (workflowDefinitionNumber === '') {
        await unbindWorkflow('form-template', Number(id));
      } else {
        await bindWorkflow('form-template', Number(id), workflowDefinitionNumber);
      }

      savedWorkflowDefinitionNumber.current = workflowDefinitionNumber;
    } catch {
      enqueueError('workflows.binding.saveFailed');
    }
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) return;

    const newItems = reorder(orderOfApprovers, source.index, destination.index);

    setOrderOfApprovers(newItems);
  };

  const getAvailableTypes = async () => {
    const module = selectedModule?.formModuleType;

    if (module !== undefined) {
      const newAvailableTypes = await getFormTypes(module);

      setAvailableTypes(newAvailableTypes);
    }
  };

  const clearState = () => {
    setNumPages(null);
    setPageNumber(1);
    setSelectedModule(null);
    setSelectedType(null);
    setFormViewport(null);
    setElements([]);
    setSelectedElement(null);
    setPageNumberScale(1);
    setRelatedFormTemplates([]);
    setCollaborators([]);
    setEmailers([]);
    setCustomApprovers([]);
    setFormApprovalSteps([]);
    setOrderOfApprovers([]);
    setOrderOfIntegrationApprovers([]);
    setIsDocLogixIntegration(false);
    setDeletedElements([]);
    setAgreeDelete(false);
    setFormTags([]);
    setDepartmentIds([]);
  };

  const elementsOrder = [
    FormElementType.StaticText,
    FormElementType.InputField,
    FormElementType.Dropdown,
    FormElementType.Checkbox,
    FormElementType.RadioButton,
    FormElementType.Date,
    FormElementType.DateRange,
    FormElementType.Image,
    FormElementType.CustomList,
    FormElementType.AddressBook,
    FormElementType.Hyperlink,
    FormElementType.Signature,
    FormElementType.User,
    FormElementType.RegistrationNumber,
  ];

  const handleCreateSteps = () => {
    let step = 1;
    const newFormApprovalSteps: FormApprovalStepProps[] = [];
    const order = isDocLogixIntegration ? orderOfIntegrationApprovers : orderOfApprovers;

    for (let i = 0; i < order.length; i++) {
      if (order[i].checked && order[i].approvalType !== FormApprovalType.DefaultSignersApproval) {
        const newStep: FormApprovalStepProps = {
          order: step,
          approvalType: order[i].approvalType,
          signerEmails: null,
        };

        step++;
        newFormApprovalSteps.push(newStep);
      }

      if (
        order[i].checked &&
        order[i].approvalType === FormApprovalType.DefaultSignersApproval &&
        order[i].signerEmails &&
        order[i].signerEmails.length > 0
      ) {
        const newStep: FormApprovalStepProps = {
          order: step,
          approvalType: order[i].approvalType,
          signerEmails:
            order[i].signerEmails && order[i].signerEmails.length > 0
              ? order[i].signerEmails.join(',')
              : null,
        };

        step++;
        newFormApprovalSteps.push(newStep);
      }
    }

    setFormApprovalSteps(newFormApprovalSteps);
  };

  const handlePdfNavButtonClick = (i: number) => {
    setPageNumber(pageNumber + i);
  };

  const onDocumentLoadSuccess = async (pdf: any) => {
    const { numPages } = pdf;
    const viewportPages: FormViewportProps[] = [];
    const layerWidth = layerSize?.width;

    setNumPages(numPages);

    for (let i = 0; i < numPages; i++) {
      let page;
      try {
        page = await pdf.getPage(i + 1);
      } catch (e) {
        enqueueError(e);
      }

      const viewport = page.getViewport({ scale: 1 });
      const { width, height } = viewport;

      if (i === 0 && layerWidth !== undefined) {
        setPageNumberScale(layerWidth / width);
      }

      viewportPages.push({
        pageNumber: i + 1,
        width,
        height,
        scale: layerWidth ? layerWidth / width : 1,
      });
    }

    setFormViewport(viewportPages);
  };

  // Map of element types to their initial values for cleaner element creation
  // Memoized to avoid recreating on every render
  const elementInitValuesMap = useMemo(
    () => ({
      [FormElementType.StaticText]: staticTextInitValues,
      [FormElementType.InputField]: inputFieldInitValues,
      [FormElementType.Dropdown]: dropdownInitValues,
      [FormElementType.Checkbox]: checkboxInitValues,
      [FormElementType.RadioButton]: radioInitValues,
      [FormElementType.Date]: dateInitValues,
      [FormElementType.DateRange]: dateRangeInitValues,
      [FormElementType.CustomList]: customListInitValues,
      [FormElementType.AddressBook]: addressBookInitValues,
      [FormElementType.Image]: imageInitValues,
      [FormElementType.Hyperlink]: hyperlinkInitValues,
      [FormElementType.Signature]: signatureInitValues,
      [FormElementType.User]: userInitValues,
      [FormElementType.RegistrationNumber]: registrationNumberInitValues,
    }),
    [
      staticTextInitValues,
      inputFieldInitValues,
      dropdownInitValues,
      checkboxInitValues,
      radioInitValues,
      dateInitValues,
      dateRangeInitValues,
      customListInitValues,
      addressBookInitValues,
      imageInitValues,
      hyperlinkInitValues,
      signatureInitValues,
      userInitValues,
      registrationNumberInitValues,
    ]
  );

  const handleElementButtonClick = useCallback(
    (type: number) => {
      const initValues = elementInitValuesMap[type];

      if (!initValues) {
        console.warn(`Unknown element type: ${type}`);
        return;
      }

      // Elements that have special rendering (skipDefaultRender)
      // Note: Checkbox, RadioButton, Date, DateRange, Signature now get collaboratorId for background colors
      const skipDefaultRenderTypes = [
        FormElementType.Image,
        FormElementType.StaticText,
        FormElementType.Hyperlink,
        FormElementType.User,
        FormElementType.RegistrationNumber,
      ];

      // Only assign collaboratorId to default elements (not skipDefaultRender)
      const shouldAssignContributor = !skipDefaultRenderTypes.includes(type);
      const ownerContributor = collaborators.find((c) => c.role === 'owner');

      const newElementId = uuidv4();
      const newElement = {
        id: newElementId,
        type,
        page: pageNumber,
        ...baseElementInitValues,
        ...initValues,
        ...(shouldAssignContributor && ownerContributor?.collaboratorId
          ? { collaboratorId: ownerContributor.collaboratorId }
          : {}),
      };

      setElements([...elements, newElement]);

      // Add element ID to owner's fields array
      if (shouldAssignContributor && ownerContributor) {
        const updatedContributors = collaborators.map((c) => {
          if (c.collaboratorId === ownerContributor.collaboratorId) {
            return {
              ...c,
              fields: [...c.fields, newElementId],
            };
          }
          return c;
        });
        setCollaborators(updatedContributors);
      }
    },
    [elementInitValuesMap, pageNumber, baseElementInitValues, elements, collaborators]
  );

  const handleDragStart = useCallback((id: string) => {
    setDragResizeElement(id);
  }, []);

  const handleDragStop = useCallback(
    (id: string, d: DraggableData) => {
      const newElements = elements.map((e) => {
        if (e.id === id) {
          e.x = d.x / pageNumberScale;
          e.y = d.y / pageNumberScale;

          return e;
        }

        return e;
      });

      setElements(newElements);
      setDragResizeElement(null);
    },
    [elements, pageNumberScale]
  );

  const handleResizeStart = useCallback((id: string) => {
    setDragResizeElement(id);
  }, []);

  const handleResizeStop = useCallback(
    (id: string, delta: ResizableDelta, position: Position) => {
      const newElements = elements.map((e) => {
        if (e.id === id) {
          if (delta.width !== 0) {
            e.width += delta.width / pageNumberScale;
          }

          if (delta.height !== 0) {
            e.height += delta.height / pageNumberScale;
          }

          e.x = position.x / pageNumberScale;
          e.y = position.y / pageNumberScale;

          return e;
        }

        return e;
      });

      setElements(newElements);
      setDragResizeElement(null);
    },
    [elements, pageNumberScale]
  );

  const handleSettingsButtonClick = useCallback((el: ElementProps) => {
    setSelectedElement(el);
    setIsDialogOpen(true);
  }, []);

  const handleElementChange = useCallback((el: ElementProps) => {
    let oldElement: ElementProps | undefined;

    setElements((prevElements) => {
      oldElement = prevElements.find((element) => element.id === el.id);
      // Replace element at same index to preserve z-order
      return prevElements.map((element) => (element.id === el.id ? el : element));
    });

    // Handle collaboratorId change - move element ID from old collaborator to new
    setCollaborators((prevCollaborators) => {
      if (oldElement?.collaboratorId !== el.collaboratorId) {
        return prevCollaborators.map((c) => {
          // Remove from old collaborator
          if (c.collaboratorId === oldElement?.collaboratorId) {
            return {
              ...c,
              fields: c.fields.filter((fieldId) => fieldId !== el.id),
            };
          }
          // Add to new collaborator
          if (c.collaboratorId === el.collaboratorId) {
            return {
              ...c,
              fields: [...c.fields, el.id],
            };
          }
          return c;
        });
      }
      return prevCollaborators;
    });
  }, []);

  const handleDeleteElement = useCallback(
    (id?: string) => {
      if (!id) {
        id = selectedElement?.id;
      }

      const newElements = [...elements];
      const elementIndex = newElements.findIndex((e) => e.id === id);
      const deletedElement = newElements[elementIndex];

      setDeletedElements([...deletedElements, deletedElement]);

      if (elementIndex > -1) {
        newElements.splice(elementIndex, 1);
        setElements(newElements);

        // Remove element ID from collaborator's fields array
        if (deletedElement?.collaboratorId) {
          setCollaborators((prevCollaborators) =>
            prevCollaborators.map((c) => {
              if (c.collaboratorId === deletedElement.collaboratorId) {
                return {
                  ...c,
                  fields: c.fields.filter((fieldId) => fieldId !== id),
                };
              }
              return c;
            })
          );
        }
      }
    },
    [elements, selectedElement, deletedElements]
  );

  const handleRestoreDeletedElement = useCallback(
    (id: string) => {
      const element = deletedElements.filter((e) => e.id === id);

      setElements([...elements, ...element]);
      setDeletedElements(deletedElements.filter((e) => e.id !== id));

      // Add element ID back to collaborator's fields array
      const restoredElement = element[0];
      if (restoredElement?.collaboratorId) {
        const updatedContributors = collaborators.map((c) => {
          if (c.collaboratorId === restoredElement.collaboratorId) {
            return {
              ...c,
              fields: [...c.fields, id],
            };
          }
          return c;
        });
        setCollaborators(updatedContributors);
      }
    },
    [deletedElements, elements, collaborators]
  );

  const handleFormTemplateInputChange = (value: RelatedFormTemplatesProps | null) => {
    const newSelectedFormTemplate = formTemplatesList.find((t) => t.id === value?.id);

    if (newSelectedFormTemplate) {
      setSelectedFormTemplate(newSelectedFormTemplate);
    }
  };

  const handleDepartmentInputChange = (value: Department | null) => {
    if (value) {
      setSelectedDepartment(value);
    }
  };

  const handleAddRelatedFormTemplate = () => {
    if (selectedFormTemplate) {
      setRelatedFormTemplates([...relatedFormTemplates, selectedFormTemplate.id]);
      setSelectedFormTemplate(null);
    }
  };

  const handleAddDepartment = () => {
    if (selectedDepartment) {
      setDepartmentIds([...departmentIds, selectedDepartment.id]);
      setSelectedDepartment(null);
    }
  };

  const handleDeleteRelatedFormTemplate = (index: number) => {
    const newRelatedFormTemplates = [...relatedFormTemplates];

    newRelatedFormTemplates.splice(index, 1);
    setRelatedFormTemplates(newRelatedFormTemplates);
  };

  const handleDeleteDepartment = (index: number) => {
    const newEmailToDepartments = [...departmentIds];

    newEmailToDepartments.splice(index, 1);
    setDepartmentIds(newEmailToDepartments);
  };

  const handleOnCloseDialog = () => {
    setIsAddTypeDialogOpen(!isAddTypeDialogOpen);
  };

  const handleOnSaveDialog = () => {
    getAvailableTypes().then();
    handleOnCloseDialog();
  };

  const handleItemChange = (item: DraggableApproverProps) => {
    if (isDocLogixIntegration) {
      const findIndex = orderOfIntegrationApprovers.findIndex(
        (approver) => approver.id === item.id
      );

      const newOrderOfIntegrationApprovers = [...orderOfIntegrationApprovers];

      newOrderOfIntegrationApprovers[findIndex] = item;

      setOrderOfIntegrationApprovers(newOrderOfIntegrationApprovers);
    } else {
      const findIndex = orderOfApprovers.findIndex((approver) => approver.id === item.id);

      const newOrderOfApprovers = [...orderOfApprovers];

      newOrderOfApprovers[findIndex] = item;

      setOrderOfApprovers(newOrderOfApprovers);
    }
  };

  const getAvailableModules = () => {
    const modules: ModuleProps[] = [];

    if (modulesData.modules.includes(ModuleTypes.HR)) {
      modules.push({
        formModuleType: DocumentModuleTypes.HR,
        moduleTitle: t('formTemplates.modules.hr'),
      });
    }

    if (modulesData.modules.includes(ModuleTypes.Accounting)) {
      modules.push({
        formModuleType: DocumentModuleTypes.Accounting,
        moduleTitle: t('formTemplates.modules.accounting'),
      });
    }

    setAvailableModules(modules);
  };

  const fetchFormTemplatesList = async () => {
    const list = await getFormTemplatesList();
    const newFormTemplatesList = list.items.map((t: FormTypeTemplateProps) => ({
      id: t.id,
      name: t.name,
    }));

    setFormTemplatesList(newFormTemplatesList);
  };

  useEffect(() => {
    handleCreateSteps();
  }, [orderOfApprovers, orderOfIntegrationApprovers]);

  useEffect(() => {
    if (elementLayer.current) {
      setLayerSize({
        width: elementLayer.current.offsetWidth,
        height: elementLayer.current.offsetHeight,
      });
    }

    if (formViewport !== null) {
      const pageNumberScale = formViewport[pageNumber - 1].scale;
      setPageNumberScale(pageNumberScale);
    }
  }, [pageNumber]);

  useEffect(() => {
    getAvailableTypes().then();
  }, [selectedModule]);

  useEffect(() => {
    if (form !== null) {
      const {
        id,
        name,
        formModuleType,
        formTypeId,
        approvalInDoclogix,
        managerApproval,
        ceoApproval,
        sortWeight,
        doclogixCoreUrlShortname,
        doclogixCoreProfileId,
        doclogixCoreFileId,
        relatedFormTemplates,
        departmentIds,
        structure,
        tags,
        mailGetterEmails,
        defaultSigners,
        defaultFormTemplate,
        approvalSteps,
        collaborators,
      } = form;

      if (tags) {
        setFormTags(tags);
      }

      if (mailGetterEmails) {
        setEmailers(mailGetterEmails);
      }

      if (defaultSigners) {
        setCustomApprovers(defaultSigners);
      }

      if (approvalSteps) {
        const isDLXIntegration = approvalSteps.find(
          (step: FormApprovalStepProps) => step.approvalType === FormApprovalType.DocLogixApproval
        );

        if (isDLXIntegration) {
          setIsDocLogixIntegration(true);

          const newApprovers = DLX_APPROVERS.map((approver) => {
            if (approver.approvalType === FormApprovalType.DocLogixApproval) {
              return { ...approver, checked: true };
            }

            if (approver.approvalType === FormApprovalType.ManagerApproval) {
              const isManager = approvalSteps.find(
                (step: FormApprovalStepProps) =>
                  step.approvalType === FormApprovalType.ManagerApproval
              );

              return { ...approver, checked: !!isManager };
            }

            return approver;
          });

          setOrderOfIntegrationApprovers(newApprovers);
          setOrderOfApprovers(APPROVERS);
        } else {
          if (approvalSteps.length === 0) {
            setOrderOfApprovers(APPROVERS);
          } else {
            let newOrderOfApprovers: DraggableApproverProps[] = [];

            for (let i = 0; i < approvalSteps.length; i++) {
              const findApprover = APPROVERS.find(
                (approver) => approver.approvalType === approvalSteps[i].approvalType
              );

              if (findApprover) {
                newOrderOfApprovers[approvalSteps[i].order - 1] = {
                  ...findApprover,
                  order: approvalSteps[i].order,
                  checked: true,
                  signerEmails:
                    approvalSteps[i].signerEmails && approvalSteps[i].signerEmails.length > 0
                      ? approvalSteps[i].signerEmails.split(',')
                      : null,
                };
              }
            }

            let order = approvalSteps.length + 1;

            for (let i = 0; i < APPROVERS.length; i++) {
              const findApprover = newOrderOfApprovers.find(
                (approver) => approver.approvalType === APPROVERS[i].approvalType
              );

              if (!findApprover) {
                newOrderOfApprovers.push({ ...APPROVERS[i], order });
                order++;
              }
            }

            setOrderOfApprovers(newOrderOfApprovers);
          }

          setOrderOfIntegrationApprovers(DLX_APPROVERS);
        }

        setFormApprovalSteps(approvalSteps);
      }

      // FORMIK
      formik.setValues({
        id,
        name,
        formModuleType,
        formTypeId,
        approvalInDoclogix,
        managerApproval,
        ceoApproval,
        sortWeight,
        doclogixCoreUrlShortname: doclogixCoreUrlShortname || '',
        doclogixCoreProfileId,
        doclogixCoreFileId,
        relatedFormTemplates,
        departmentIds,
        defaultFormTemplate,
        approvalSteps,
        tags: formTags.length > 0 ? formTags.map((item) => item.id) : [],
      });

      // MODULE
      const filteredModule = availableModules.find((m) => m.formModuleType === formModuleType);

      if (filteredModule !== undefined) {
        setSelectedModule(filteredModule);
      }

      // ELEMENTS
      if (structure !== null) {
        const elementsStructure = JSON.parse(structure);
        setElements([...elementsStructure]);
      }

      if (relatedFormTemplates) {
        setRelatedFormTemplates(relatedFormTemplates);
      }

      if (departmentIds) {
        setDepartmentIds(departmentIds);
      }

      if (collaborators && collaborators.length > 0) {
        setCollaborators(collaborators);
      }
    }
  }, [form]);

  useEffect(() => {
    if (deletedElements.length === 0) {
      setAgreeDelete(false);
    }
  }, [deletedElements]);

  useEffect(() => {
    const filteredType = availableTypes.filter((t) => t.id === formik.values.formTypeId);

    if (filteredType.length > 0) {
      setSelectedType(filteredType[0]);
    }
  }, [availableTypes]);

  // Ensure owner collaborator always exists, regardless of module status
  useEffect(() => {
    const hasOwner = collaborators.some((c) => c.role === 'owner');

    if (!hasOwner && collaborators.length === 0) {
      const ownerCollaborator: Collaborator = {
        collaboratorId: uuidv4(),
        role: 'owner',
        email: undefined,
        fields: [],
        color: 'rgba(208, 242, 255, 0.7)',
        canFill: true,
        order: collaborators.length,
      };

      setCollaborators([ownerCollaborator]);
    }
  }, [collaborators, t]);

  useEffect(() => {
    clearState();
    clearFormsState();
    fetchFormTemplatesList().then();
    getAvailableModules();

    if (id !== undefined) {
      getFile(parseInt(id, 10)).then();
      getForm(parseInt(id, 10)).then();
    }

    if (!departments) {
      getDepartments({ searchQuery: null }).then();
    }
  }, []);

  return (
    <Card>
      <CardHeader
        title={
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 9,
              }}
            >{`${t(titleLocale)}: ${form ? form?.name : ''}`}</Grid>

            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <FormControlLabel
                control={<Switch />}
                onClick={() => setIsEditMode(!isEditMode)}
                label={t('forms.preview')}
              />
            </Grid>
          </Grid>
        }
      />
      <FormikProvider value={formik}>
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  md: 9,
                }}
              >
                <Card>
                  <CardContent>
                    <FormPdfViewer
                      file={file}
                      numPages={numPages}
                      pageNumber={pageNumber}
                      isEditMode={isEditMode}
                      elements={elements}
                      collaborators={collaborators}
                      formViewport={formViewport}
                      pageNumberScale={pageNumberScale}
                      layerSize={layerSize}
                      isDialogOpen={isDialogOpen}
                      selectedElement={selectedElement}
                      elementLayer={elementLayer}
                      onDocumentLoadSuccess={onDocumentLoadSuccess}
                      handlePdfNavButtonClick={handlePdfNavButtonClick}
                      handleSettingsButtonClick={handleSettingsButtonClick}
                      handleDragStart={handleDragStart}
                      handleDragStop={handleDragStop}
                      handleResizeStart={handleResizeStart}
                      handleResizeStop={handleResizeStop}
                      handleDeleteElement={handleDeleteElement}
                      handleElementChange={handleElementChange}
                      setElements={setElements}
                      setIsDialogOpen={setIsDialogOpen}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 3,
                }}
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                {/* Collaborator */}
                <ModuleCheck module={ModuleTypes.CollabForms}>
                  <Card>
                    <Card sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'left' }}>
                      <CardHeader title={t('forms.collaborators')} />

                      <CardContent>
                        <CollaboratorsEditForm
                          collaborators={collaborators}
                          setCollaborators={setCollaborators}
                          elements={elements}
                        />
                      </CardContent>
                    </Card>
                  </Card>
                </ModuleCheck>

                {/* Elements */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Card sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'left' }}>
                    <CardHeader title={t('forms.elementsTitle')} />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {elementsOrder.map((el, i) => (
                            <FormsElementButton
                              key={`element-${i + 1}`}
                              type={el}
                              onClick={handleElementButtonClick}
                            />
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  <FormApprovers
                    isDocLogixIntegration={isDocLogixIntegration}
                    setIsDocLogixIntegration={setIsDocLogixIntegration}
                    items={isDocLogixIntegration ? orderOfIntegrationApprovers : orderOfApprovers}
                    handleItemChange={handleItemChange}
                    onDragEnd={onDragEnd}
                    disabled={hasWorkflow}
                    disabledReasonLocale="forms.approversDisabledByWorkflow"
                  />

                  {id && (
                    <ModuleCheck module={ModuleTypes.WorkflowsModule}>
                      <FormWorkflowBinding
                        formTemplateId={Number(id)}
                        disabled={hasApprovers}
                        disabledReasonLocale="workflows.binding.disabledByApprovers"
                        onLoaded={handleWorkflowLoaded}
                        onSelectionChange={setWorkflowDefinitionNumber}
                      />
                    </ModuleCheck>
                  )}

                  <FormEmailReceivers
                    isExpanded={isEmailReceiversExpanded}
                    setIsExpanded={setIsEmailReceiversExpanded}
                    emailers={emailers}
                    setEmailers={setEmailers}
                    departments={departments}
                    departmentIds={departmentIds}
                    selectedDepartment={selectedDepartment}
                    handleDepartmentInputChange={handleDepartmentInputChange}
                    handleAddDepartment={handleAddDepartment}
                    handleDeleteDepartment={handleDeleteDepartment}
                  />

                  <FormSettings
                    formik={formik}
                    isExpanded={isSettingsExpanded}
                    setIsExpanded={setIsSettingsExpanded}
                    availableModules={availableModules}
                    availableTypes={availableTypes}
                    selectedModule={selectedModule}
                    selectedType={selectedType}
                    formTags={formTags}
                    setSelectedModule={setSelectedModule}
                    setFormTags={setFormTags}
                    setIsAddTypeDialogOpen={setIsAddTypeDialogOpen}
                  />

                  <FormRelatedForms
                    isExpanded={isRelatedFormsExpanded}
                    setIsExpanded={setIsRelatedFormsExpanded}
                    formTemplatesList={formTemplatesList}
                    relatedFormTemplates={relatedFormTemplates}
                    selectedFormTemplate={selectedFormTemplate}
                    currentFormId={formik.values.id}
                    handleFormTemplateInputChange={handleFormTemplateInputChange}
                    handleAddRelatedFormTemplate={handleAddRelatedFormTemplate}
                    handleDeleteRelatedFormTemplate={handleDeleteRelatedFormTemplate}
                  />
                </Box>

                {/* Action */}
                {deletedElements.length > 0 && (
                  <Box>
                    <Typography variant="h6">{t('forms.deletedElements')}:</Typography>

                    {deletedElements.map((el, i) => (
                      <Box
                        key={`${el.label}-${i}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 2,
                          ml: 1,
                        }}
                      >
                        <Typography variant="subtitle2">{el.label}</Typography>

                        <Tooltip title={t('forms.restoreElement')}>
                          <IconButton
                            size="small"
                            id={`yd0sowN7-${i}`}
                            onClick={() => handleRestoreDeletedElement(el.id)}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box>
                  {deletedElements.length > 0 && (
                    <FormGroup sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={agreeDelete}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              setAgreeDelete(event.target.checked);
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption">{t('forms.dataLossConsent')}</Typography>
                        }
                      />
                    </FormGroup>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="medium"
                    fullWidth
                    id="yd0sown5"
                    disabled={deletedElements.length > 0 && !agreeDelete}
                  >
                    {t('forms.buttons.save')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Form>
      </FormikProvider>
      <TypeAddEditDialog
        onClose={handleOnCloseDialog}
        onSave={handleOnSaveDialog}
        formId={-1}
        isTypeAddEditDialogOpen={isAddTypeDialogOpen}
      />
    </Card>
  );
};

export default FormsEditForm;
